// components/LegalRepresentatives/RepresentativeDetailModal.js
import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import legalRepresentativeDetailService from '../service/api/legalRepresentativeDetailService';
import { generateReference } from '../service/utils/referenceGenerator';

/**
 * Composant modal pour afficher les détails complets d'un représentant légal
 * Utilise l'API /api/LegalRepresentatives/{representativeId}
 * @param {Object} props - Propriétés du composant
 * @param {boolean} props.isOpen - État d'ouverture du modal
 * @param {Function} props.onClose - Fonction pour fermer le modal
 * @param {Object} props.representative - Représentant légal sélectionné (avec au moins l'ID)
 */
const RepresentativeDetailModal = ({ isOpen, onClose, representative }) => {
  // États pour les données et le chargement
  const [representativeData, setRepresentativeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Charger les détails du représentant quand le modal s'ouvre
   */
  useEffect(() => {
    const fetchRepresentativeDetail = async () => {
      if (!isOpen || !representative?.id) {
        setRepresentativeData(null);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      setRepresentativeData(null);

      try {
        const result = await legalRepresentativeDetailService.getRepresentativeDetail(
          representative.id
        );

        if (result.success) {
          setRepresentativeData(result.data);
        } else {
          setError(result.error || 'Erreur lors du chargement des détails');
        }
      } catch (err) {
        console.error('Error in fetchRepresentativeDetail:', err);
        setError('Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    };

    fetchRepresentativeDetail();
  }, [isOpen, representative]);

  /**
   * Formater un numéro de téléphone
   * @param {string} phoneNumber - Numéro de téléphone
   * @returns {string} Numéro formaté
   */
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'Non renseigné';
    
    // Format +XX XXX XX XX XX
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 9) {
      return `+237 ${cleaned.match(/.{1,2}/g).join(' ')}`;
    }
    
    return phoneNumber;
  };

  /**
   * Obtenir l'initial pour l'avatar
   * @param {string} name - Nom du représentant
   * @returns {string} Initiale
   */
  const getAvatarInitial = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  // Si le modal n'est pas ouvert, ne rien rendre
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          
          {/* En-tête du modal */}
          <div className="modal-header bg-gradient-primary text-white border-0">
            <div className="d-flex align-items-center w-100">
              <div className="avatar-circle bg-white text-primary me-3">
                <span className="avatar-initial fw-bold">
                  {getAvatarInitial(representativeData?.representativeDisplayName)}
                </span>
              </div>
              <div className="flex-grow-1">
                <h5 className="modal-title mb-0 fw-bold">
                  {representativeData?.representativeDisplayName || 'Détails du Représentant'}
                </h5>
              </div>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>
          </div>

          {/* Corps du modal */}
          <div className="modal-body p-4">
            {/* État de chargement */}
            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-3 text-muted fw-medium">Chargement des détails du représentant...</p>
              </div>
            )}

            {/* Message d'erreur */}
            {error && !loading && (
              <div className="alert alert-danger border-0 shadow-sm">
                <div className="d-flex align-items-center">
                  <i className="fas fa-exclamation-circle fa-2x me-3"></i>
                  <div>
                    <h6 className="alert-heading mb-1">Erreur de chargement</h6>
                    <p className="mb-0">{error}</p>
                    <button 
                      className="btn btn-sm btn-outline-danger mt-2"
                      onClick={() => window.location.reload()}
                    >
                      <i className="fas fa-redo me-1"></i>
                      Réessayer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Détails du représentant */}
            {!loading && !error && representativeData && (
              <div className="row g-4">
                
                {/* Section 1: Informations principales */}
                <div className="col-lg-8">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-transparent border-bottom">
                      <h6 className="mb-0 fw-bold text-primary">
                        <i className="fas fa-info-circle me-2"></i>
                        Informations principales
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        
                        {/* Colonne gauche - Représentant */}
                        <div className="col-md-6">
                          <div className="mb-4">
                            <label className="form-label small text-uppercase text-muted fw-bold mb-2">
                              Représentant légal
                            </label>
                            <div className="d-flex align-items-center mb-3">
                              <div className="avatar-sm bg-primary bg-opacity-10 text-white rounded-circle me-3">
                                <i className="fas fa-user-tie"></i>
                              </div>
                              <div>
                                <h5 className="mb-0 fw-bold">
                                  {representativeData.representativeDisplayName}
                                </h5>
                              </div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label small text-muted mb-1">Email</label>
                            <div className="d-flex align-items-center">
                              <i className="fas fa-envelope text-muted me-2"></i>
                              <a 
                                href={`mailto:${representativeData.representativeEmail}`} 
                                className="text-decoration-none text-primary fw-medium"
                              >
                                {representativeData.representativeEmail}
                              </a>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label small text-muted mb-1">Téléphone</label>
                            <div className="d-flex align-items-center">
                              <i className="fas fa-phone text-muted me-2"></i>
                              <span className="fw-medium">
                                {formatPhoneNumber(representativeData.representativePhoneNumber)}
                              </span>
                            </div>
                          </div>

                          {representativeData.representativeCompanyName && (
                            <div className="mb-3">
                              <label className="form-label small text-muted mb-1">Entreprise</label>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-building text-muted me-2"></i>
                                <span className="fw-medium">
                                  {representativeData.representativeCompanyName}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Colonne droite - Compte géré */}
                        <div className="col-md-6">
                          <div className="mb-4">
                            <label className="form-label small text-uppercase text-muted fw-bold mb-2">
                              Compte géré
                            </label>
                            <div className="d-flex align-items-center mb-3">
                              <div className="avatar-sm bg-success bg-opacity-10 text-success rounded-circle me-3">
                                <i className="fas fa-user-check"></i>
                              </div>
                              <div>
                                <h5 className="mb-0 fw-bold">
                                  {representativeData.managedAccountDisplayName}
                                </h5>
                              </div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label small text-muted mb-1">Prénom</label>
                            <div className="d-flex align-items-center">
                              <i className="fas fa-user text-muted me-2"></i>
                              <span className="fw-medium">
                                {representativeData.managedAccountFirstName}
                              </span>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label small text-muted mb-1">Nom</label>
                            <div className="d-flex align-items-center">
                              <i className="fas fa-user text-muted me-2"></i>
                              <span className="fw-medium">
                                {representativeData.managedAccountLastName}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Informations du mandat */}
                <div className="col-lg-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-transparent border-bottom">
                      <h6 className="mb-0 fw-bold text-primary">
                        <i className="fas fa-file-contract me-2"></i>
                        Informations du mandat
                      </h6>
                    </div>
                    <div className="card-body">
                      
                      <div className="mb-4">
                        <label className="form-label small text-muted mb-2">Type de mandat</label>
                        <div className="d-flex align-items-center">
                          <i className={`${legalRepresentativeDetailService.getMandateIcon(representativeData.mandateType)} fa-lg me-3 text-primary`}></i>
                          <div>
                            <span className={`badge rounded-pill ${legalRepresentativeDetailService.getMandateBadgeClass(representativeData.mandateType)} px-3 py-2`}>
                              {legalRepresentativeDetailService.formatMandateType(representativeData.mandateType)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="form-label small text-muted mb-2">Relation</label>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-handshake fa-lg me-3 text-info"></i>
                          <span className="fw-medium">
                            {legalRepresentativeDetailService.formatRelationshipType(representativeData.relationshipType)}
                          </span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="form-label small text-muted mb-2">Statut</label>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-circle fa-lg me-3" style={{ 
                            color: representativeData.status === 'ACTIVE' ? '#28a745' : 
                                   representativeData.status === 'PENDING' ? '#ffc107' : '#6c757d'
                          }}></i>
                          <span className={`badge rounded-pill ${legalRepresentativeDetailService.getStatusBadgeClass(representativeData.status)} px-3 py-2`}>
                            {representativeData.status === 'ACTIVE' ? 'Actif' :
                             representativeData.status === 'PENDING' ? 'En attente' :
                             representativeData.status === 'INACTIVE' ? 'Inactif' :
                             representativeData.status === 'EXPIRED' ? 'Expiré' : representativeData.status}
                          </span>
                        </div>
                      </div>

                      {representativeData.mandateProof && (
                        <div className="mb-4">
                          <label className="form-label small text-muted mb-2">Preuve de mandat</label>
                          <div className="d-flex align-items-center">
                            <i className="fas fa-file-pdf fa-lg me-3 text-danger"></i>
                            <span className="fw-medium text-truncate">
                              {representativeData.mandateProof}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section 3: Dates et chronologie */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-transparent border-bottom">
                      <h6 className="mb-0 fw-bold text-primary">
                        <i className="fas fa-calendar-alt me-2"></i>
                        Dates et chronologie
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        
                        <div className="col-md-4">
                          <div className="timeline-item">
                            <div className="timeline-marker bg-primary"></div>
                            <div className="timeline-content">
                              <label className="form-label small text-muted mb-1">Date de début</label>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-play-circle fa-lg me-3 text-primary"></i>
                                <div>
                                  <h6 className="mb-0 fw-bold">
                                    {legalRepresentativeDetailService.formatDate(representativeData.startDate)}
                                  </h6>
                                  <small className="text-muted">
                                    Début du mandat
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="timeline-item">
                            <div className="timeline-marker bg-success"></div>
                            <div className="timeline-content">
                              <label className="form-label small text-muted mb-1">Date de création</label>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-plus-circle fa-lg me-3 text-success"></i>
                                <div>
                                  <h6 className="mb-0 fw-bold">
                                    {legalRepresentativeDetailService.formatDate(representativeData.creationDate)}
                                  </h6>
                                  <small className="text-muted">
                                    Enregistrement dans le système
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="timeline-item">
                            <div className="timeline-marker bg-info"></div>
                            <div className="timeline-content">
                              <label className="form-label small text-muted mb-1">Statut actuel</label>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-sync-alt fa-lg me-3 text-info"></i>
                                <div>
                                  <h6 className="mb-0 fw-bold">
                                    {representativeData.status === 'ACTIVE' ? 'En cours' : 'Terminé'}
                                  </h6>
                                  <small className="text-muted">
                                    {representativeData.status === 'ACTIVE' ? 'Mandat actif' : 'Mandat terminé'}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 4: Résumé et identifiants */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-transparent border-bottom">
                      <h6 className="mb-0 fw-bold text-primary">
                        <i className="fas fa-fingerprint me-2"></i>
                        Identifiants et métadonnées
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        
                        <div className="col-md-3">
                          <div className="info-card">
                            <div className="info-icon bg-primary bg-opacity-10">
                              <i className="fas fa-hashtag text-primary"></i>
                            </div>
                            <div className="info-content">
                              <label className="small text-muted mb-1">ID Représentant</label>
                              <h6 className="mb-0 fw-bold">{generateReference(representativeData.id)}</h6>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="info-card">
                            <div className="info-icon bg-success bg-opacity-10">
                              <i className="fas fa-user-circle text-success"></i>
                            </div>
                            <div className="info-content">
                              <label className="small text-muted mb-1">ID Compte Rep.</label>
                              <h6 className="mb-0 fw-bold">{generateReference(representativeData.representativeAccountId)}</h6>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="info-card">
                            <div className="info-icon bg-info bg-opacity-10">
                              <i className="fas fa-user text-info"></i>
                            </div>
                            <div className="info-content">
                              <label className="small text-muted mb-1">ID Compte Géré</label>
                              <h6 className="mb-0 fw-bold">{generateReference(representativeData.accountId)}</h6>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="info-card">
                            <div className="info-icon bg-warning bg-opacity-10">
                              <i className="fas fa-link text-warning"></i>
                            </div>
                            <div className="info-content">
                              <label className="small text-muted mb-1">Type de liaison</label>
                              <h6 className="mb-0 fw-bold">
                                {representativeData.relationshipType === 'PARENT' ? 'Parentale' : 'Légale'}
                              </h6>
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* Aucune donnée */}
            {!loading && !error && !representativeData && (
              <div className="text-center py-5">
                <div className="empty-state">
                  <i className="fas fa-user-slash fa-4x text-muted mb-4"></i>
                  <h5 className="text-muted mb-3">Aucune donnée disponible</h5>
                  <p className="text-muted mb-4">
                    Les détails de ce représentant légal ne sont pas disponibles.
                  </p>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={onClose}
                  >
                    <i className="fas fa-times me-1"></i>
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Pied de page */}
          <div className="modal-footer bg-light border-top">
            <div className="d-flex justify-content-between align-items-center w-100">
              <div>
                {representativeData && (
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Dernière mise à jour: {legalRepresentativeDetailService.formatDate(new Date().toISOString())}
                  </small>
                )}
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={onClose}
                >
                  <i className="fas fa-times me-1"></i>
                  Fermer
                </button>
                {representativeData && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      // Option: Imprimer ou exporter les détails
                      window.print();
                    }}
                  >
                    <i className="fas fa-print me-1"></i>
                    Imprimer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Styles CSS inline pour une meilleure présentation
const styles = `
  .bg-gradient-primary {
    background: linear-gradient(135deg, #4b6cb7 0%, #182848 100%) !important;
  }
  
  .avatar-circle {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .avatar-sm {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }
  
  .timeline-item {
    position: relative;
    padding-left: 30px;
  }
  
  .timeline-marker {
    position: absolute;
    left: 0;
    top: 0;
    width: 12px;
    height: 12px;
    border-radius: 50%;
  }
  
  .info-card {
    display: flex;
    align-items: center;
    padding: 15px;
    border-radius: 10px;
    background: #f8f9fa;
    transition: all 0.3s ease;
  }
  
  .info-card:hover {
    background: #e9ecef;
    transform: translateY(-2px);
  }
  
  .info-icon {
    width: 50px;
    height: 50px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 15px;
    font-size: 20px;
  }
  
  .empty-state {
    max-width: 400px;
    margin: 0 auto;
  }
  
  .bg-purple {
    background-color: #6f42c1 !important;
  }
  
  .text-purple {
    color: #6f42c1 !important;
  }
`;

// Ajouter les styles au document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default RepresentativeDetailModal;